import { getCardInstance, getLandDefinitionForInstance } from "../selectors/card-instances";
import type { DevelopLandAction, GameEvent, SetupGameState } from "../types";
import type { TransitionResult } from "./advance-phase";

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function applyDevelopLand(
  state: Readonly<SetupGameState>,
  action: DevelopLandAction,
  actionSequence: number,
): TransitionResult {
  const instance = getCardInstance(state, action.cardInstanceId);
  const definition = getLandDefinitionForInstance(state, action.cardInstanceId);
  const lane = state.lanes[action.laneId];
  const side = lane.players[action.playerId];
  const land = side.land;
  if (instance === undefined || definition === undefined || land === null || land.stage === 3) {
    throw new Error("Validated Develop Land action is missing its Land state or definition.");
  }

  const stage = (land.stage + 1) as 2 | 3;
  const stageDefinition = definition.stages[stage - 1];
  if (stageDefinition === undefined) {
    throw new Error("Validated Develop Land action is missing its target Stage definition.");
  }
  const maximumControlIncrease = stageDefinition.maximumControl - land.maximumControl;
  const currentControl = Math.min(
    stageDefinition.maximumControl,
    land.currentControl + maximumControlIncrease,
  );
  const events: GameEvent[] = [
    {
      type: "land-developed",
      eventId: eventId(actionSequence, 0),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      cardInstanceId: action.cardInstanceId,
      cardDefinitionId: instance.cardDefinitionId,
      previousStage: land.stage,
      stage,
      previousCurrentControl: land.currentControl,
      currentControl,
      previousMaximumControl: land.maximumControl,
      maximumControl: stageDefinition.maximumControl,
    },
    {
      type: "land-action-used",
      eventId: eventId(actionSequence, 1),
      actionSequence,
      playerId: action.playerId,
      actionType: "develop-land",
      landActionsUsed: 1,
    },
  ];

  return {
    state: {
      ...state,
      players: {
        ...state.players,
        [action.playerId]: {
          ...state.players[action.playerId],
          hand: state.players[action.playerId].hand.filter(
            (cardInstanceId) => cardInstanceId !== action.cardInstanceId,
          ),
          actionsUsed: {
            ...state.players[action.playerId].actionsUsed,
            landActions: 1,
          },
        },
      },
      lanes: {
        ...state.lanes,
        [action.laneId]: {
          ...lane,
          players: {
            ...lane.players,
            [action.playerId]: {
              ...side,
              land: {
                ...land,
                cardInstanceIds: [...land.cardInstanceIds, action.cardInstanceId],
                stage,
                currentControl,
                maximumControl: stageDefinition.maximumControl,
              },
            },
          },
        },
      },
    },
    events,
  };
}
